import { FuturonESPLoader } from './esploader.js';
import { FuturonSTK500Loader } from './stk500loader.js';
import { parseHex } from './hexparser.js';
import { FUTURON_FIRMWARES, createSyntheticBinary, createSyntheticHex } from './firmwares.js';
import { FuturonSerialMonitor } from './serial_monitor.js';

export const SKETCH_TEMPLATES = {
  esp8266: {
    filename: 'ESP8266_Serial_LED_Control.ino',
    desc: 'Interactive sketch for <strong>ESP8266 / NodeMCU</strong>. Listens on <strong>115200 baud</strong> for serial commands (<code>LED_ON</code>, <code>LED_OFF</code>, <code>STATUS</code>, <code>PING</code>) and controls the onboard LED (GPIO 2 / D4) with instant serial status replies.',
    code: `// =========================================================================
// Futuron Hub: ESP8266 / NodeMCU Interactive Serial LED Controller
// Baud Rate: 115200 | Line Ending: Both NL & CR (\\r\\n)
// =========================================================================

#define LED_PIN 2  // D4 / GPIO2 (Built-in Blue LED, Active LOW on NodeMCU)

void setup() {
  Serial.begin(115200);
  pinMode(LED_PIN, OUTPUT);
  digitalWrite(LED_PIN, HIGH); // Turn LED OFF (Active LOW)
  delay(300);

  Serial.println();
  Serial.println(F("=================================================="));
  Serial.println(F("[ESP8266] Futuron Serial Studio Controller Ready"));
  Serial.println(F("[ESP8266] Commands: LED_ON | LED_OFF | STATUS | PING | HELP"));
  Serial.println(F("=================================================="));
}

void loop() {
  if (Serial.available() > 0) {
    String cmd = Serial.readStringUntil('\\n');
    cmd.trim();
    cmd.toUpperCase();

    if (cmd == "LED_ON" || cmd == "1" || cmd == "ON") {
      digitalWrite(LED_PIN, LOW); // Active-LOW ON
      Serial.println(F("[ESP8266] LED is ON (GPIO 2 / D4)"));
    }
    else if (cmd == "LED_OFF" || cmd == "0" || cmd == "OFF") {
      digitalWrite(LED_PIN, HIGH); // Active-LOW OFF
      Serial.println(F("[ESP8266] LED is OFF (GPIO 2 / D4)"));
    }
    else if (cmd == "STATUS" || cmd == "GET_LED") {
      bool isLedOn = (digitalRead(LED_PIN) == LOW);
      Serial.print(F("[ESP8266] LED status: "));
      Serial.print(isLedOn ? F("ON") : F("OFF"));
      Serial.print(F(" | Uptime: "));
      Serial.print(millis() / 1000);
      Serial.println(F("s"));
    }
    else if (cmd == "PING") {
      Serial.println(F("[ESP8266] PONG"));
    }
    else if (cmd == "HELP") {
      Serial.println(F("[ESP8266] Available Commands:"));
      Serial.println(F("  - LED_ON  : Turn onboard LED ON"));
      Serial.println(F("  - LED_OFF : Turn onboard LED OFF"));
      Serial.println(F("  - STATUS  : Query current LED state & uptime"));
      Serial.println(F("  - PING    : Latency test"));
    }
    else if (cmd.length() > 0) {
      Serial.print(F("[ESP8266] Unknown command: \""));
      Serial.print(cmd);
      Serial.println(F("\". Type HELP for list."));
    }
  }
}`
  },
  esp32: {
    filename: 'ESP32_Serial_LED_Control.ino',
    desc: 'Interactive sketch for <strong>ESP32 DevKit V1</strong>. Listens on <strong>115200 baud</strong> for serial commands (<code>LED_ON</code>, <code>LED_OFF</code>, <code>STATUS</code>, <code>PING</code>) and controls the onboard blue LED (GPIO 2) with instant serial status replies.',
    code: `// =========================================================================
// Futuron Hub: ESP32 Interactive Serial LED Controller
// Baud Rate: 115200 | Line Ending: Both NL & CR (\\r\\n)
// =========================================================================

#define LED_PIN 2  // Onboard Blue LED (GPIO 2, Active HIGH on ESP32 DevKit)

void setup() {
  Serial.begin(115200);
  pinMode(LED_PIN, OUTPUT);
  digitalWrite(LED_PIN, LOW); // Turn LED OFF
  delay(300);

  Serial.println();
  Serial.println(F("=================================================="));
  Serial.println(F("[ESP32] Futuron Serial Studio Controller Ready"));
  Serial.println(F("[ESP32] Commands: LED_ON | LED_OFF | STATUS | PING | HELP"));
  Serial.println(F("=================================================="));
}

void loop() {
  if (Serial.available() > 0) {
    String cmd = Serial.readStringUntil('\\n');
    cmd.trim();
    cmd.toUpperCase();

    if (cmd == "LED_ON" || cmd == "1" || cmd == "ON") {
      digitalWrite(LED_PIN, HIGH);
      Serial.println(F("[ESP32] LED is ON (GPIO 2)"));
    }
    else if (cmd == "LED_OFF" || cmd == "0" || cmd == "OFF") {
      digitalWrite(LED_PIN, LOW);
      Serial.println(F("[ESP32] LED is OFF (GPIO 2)"));
    }
    else if (cmd == "STATUS" || cmd == "GET_LED") {
      bool isLedOn = digitalRead(LED_PIN);
      Serial.print(F("[ESP32] LED status: "));
      Serial.print(isLedOn ? F("ON") : F("OFF"));
      Serial.print(F(" | Uptime: "));
      Serial.print(millis() / 1000);
      Serial.println(F("s"));
    }
    else if (cmd == "PING") {
      Serial.println(F("[ESP32] PONG"));
    }
    else if (cmd == "HELP") {
      Serial.println(F("[ESP32] Available Commands:"));
      Serial.println(F("  - LED_ON  : Turn onboard LED ON"));
      Serial.println(F("  - LED_OFF : Turn onboard LED OFF"));
      Serial.println(F("  - STATUS  : Query current LED state & uptime"));
      Serial.println(F("  - PING    : Latency test"));
    }
    else if (cmd.length() > 0) {
      Serial.print(F("[ESP32] Unknown command: \""));
      Serial.print(cmd);
      Serial.println(F("\". Type HELP for list."));
    }
  }
}`
  },
  arduino: {
    filename: 'Arduino_Serial_LED_Control.ino',
    desc: 'Interactive sketch for <strong>Arduino Uno / Nano (ATmega328P)</strong>. Listens on <strong>115200 baud</strong> for serial commands (<code>LED_ON</code>, <code>LED_OFF</code>, <code>STATUS</code>, <code>PING</code>) and controls Pin 13 LED with formatted status replies.',
    code: `// =========================================================================
// Futuron Hub: Arduino Uno / Nano Interactive Serial LED Controller
// Baud Rate: 115200 | Line Ending: Both NL & CR (\\r\\n)
// =========================================================================

#define LED_PIN 13  // Onboard Pin 13 Built-in LED

void setup() {
  Serial.begin(115200);
  pinMode(LED_PIN, OUTPUT);
  digitalWrite(LED_PIN, LOW); // Turn LED OFF
  delay(200);

  Serial.println();
  Serial.println(F("=================================================="));
  Serial.println(F("[Arduino] Futuron Serial Studio Controller Ready"));
  Serial.println(F("[Arduino] Commands: LED_ON | LED_OFF | STATUS | PING | HELP"));
  Serial.println(F("=================================================="));
}

void loop() {
  if (Serial.available() > 0) {
    String cmd = Serial.readStringUntil('\\n');
    cmd.trim();
    cmd.toUpperCase();

    if (cmd == "LED_ON" || cmd == "1" || cmd == "ON") {
      digitalWrite(LED_PIN, HIGH);
      Serial.println(F("[Arduino] LED is ON (Pin 13)"));
    }
    else if (cmd == "LED_OFF" || cmd == "0" || cmd == "OFF") {
      digitalWrite(LED_PIN, LOW);
      Serial.println(F("[Arduino] LED is OFF (Pin 13)"));
    }
    else if (cmd == "STATUS" || cmd == "GET_LED") {
      bool isLedOn = digitalRead(LED_PIN);
      Serial.print(F("[Arduino] LED status: "));
      Serial.print(isLedOn ? F("ON") : F("OFF"));
      Serial.print(F(" | Uptime: "));
      Serial.print(millis() / 1000);
      Serial.println(F("s"));
    }
    else if (cmd == "PING") {
      Serial.println(F("[Arduino] PONG"));
    }
    else if (cmd == "HELP") {
      Serial.println(F("[Arduino] Available Commands:"));
      Serial.println(F("  - LED_ON  : Turn onboard LED ON"));
      Serial.println(F("  - LED_OFF : Turn onboard LED OFF"));
      Serial.println(F("  - STATUS  : Query current LED state & uptime"));
      Serial.println(F("  - PING    : Latency test"));
    }
    else if (cmd.length() > 0) {
      Serial.print(F("[Arduino] Unknown command: \""));
      Serial.print(cmd);
      Serial.println(F("\". Type HELP for list."));
    }
  }
}`
  }
};

class FuturonFlashHubApp {
  constructor() {
    this.espLoader = new FuturonESPLoader();
    this.stkLoader = new FuturonSTK500Loader();
    this.activeLoader = this.espLoader;
    this.monitor = null;
    this.selectedPresetId = FUTURON_FIRMWARES[0].id;
    this.firmwareMode = 'preset'; // 'preset' | 'custom'
    this.currentSketchType = 'esp8266';
    this.timerInterval = null;
    this.startTime = null;

    this.initDOM();
    this.initEvents();
    this.checkBrowserSupport();
    this.renderPresets();
    this.renderSketchCode('esp8266');
  }

  initDOM() {
    // Theme Elements
    this.btnThemeToggle = document.getElementById('btn-theme-toggle');
    this.iconThemeMoon = document.getElementById('icon-theme-moon');
    this.iconThemeSun = document.getElementById('icon-theme-sun');

    // Badges & Alert
    this.elSupportBadge = document.getElementById('browser-support-badge');
    this.elStatusBadge = document.getElementById('connection-status-badge');
    this.elBrowserAlert = document.getElementById('browser-alert');

    // Summary Bar
    this.elSummaryChip = document.getElementById('summary-chip');
    this.elSummaryMac = document.getElementById('summary-mac');
    this.elSummaryFlash = document.getElementById('summary-flash');
    this.elSummaryBaud = document.getElementById('summary-baud');
    this.btnQuickConnect = document.getElementById('btn-quick-connect');
    this.btnQuickDisconnect = document.getElementById('btn-quick-disconnect');

    // Tabs
    this.tabButtons = document.querySelectorAll('.tab-btn');
    this.tabPanes = document.querySelectorAll('.tab-pane');

    // Hardware Spec Elements
    this.elChipModel = document.getElementById('chip-model');
    this.elChipRevision = document.getElementById('chip-revision');
    this.elChipMac = document.getElementById('chip-mac');
    this.elChipFlashSize = document.getElementById('chip-flash-size');
    this.elChipCrystal = document.getElementById('chip-crystal');
    this.elChipFeatures = document.getElementById('chip-features');

    // Controls
    this.selectBaud = document.getElementById('flashing-baudrate');
    this.selectChip = document.getElementById('target-chip-select');
    this.btnEraseFlash = document.getElementById('btn-erase-flash');
    this.btnResetDevice = document.getElementById('btn-reset-device');
    this.btnStartFlash = document.getElementById('btn-start-flash');
    this.chkEraseBeforeFlash = document.getElementById('chk-erase-before-flash');
    this.elHardwareAutoBadge = document.getElementById('hardware-auto-badge');
    this.elChipSelectHelp = document.getElementById('chip-select-help');
    this.elDevkitTip = document.getElementById('devkit-boot-tip');

    // Dynamic Label Spans
    this.lblBtnConnect = document.getElementById('lbl-btn-connect');
    this.lblBtnReset = document.getElementById('lbl-btn-reset');
    this.lblBtnFlash = document.getElementById('lbl-btn-flash');

    // Firmware Mode & Views
    this.btnModePreset = document.getElementById('mode-btn-preset');
    this.btnModeCustom = document.getElementById('mode-btn-custom');
    this.viewPreset = document.getElementById('view-preset-firmware');
    this.viewCustom = document.getElementById('view-custom-firmware');
    this.firmwareListContainer = document.getElementById('firmware-list');
    this.binaryRowsContainer = document.getElementById('binary-rows');
    this.btnAddBinRow = document.getElementById('btn-add-bin-row');

    // Progress & Logs
    this.elProgressStatus = document.getElementById('progress-status-text');
    this.elProgressPercentage = document.getElementById('progress-percentage');
    this.elProgressBarFill = document.getElementById('progress-bar-fill');
    this.elMetricSpeed = document.getElementById('metric-speed');
    this.elMetricBytes = document.getElementById('metric-bytes');
    this.elMetricTime = document.getElementById('metric-time');
    this.flasherLogOutput = document.getElementById('flasher-log-output');
    this.btnClearLog = document.getElementById('btn-clear-flasher-log');

    // Terminal Studio
    this.terminalOutput = document.getElementById('terminal-output');
    this.selectTerminalBaud = document.getElementById('terminal-baudrate');
    this.btnTerminalConnect = document.getElementById('btn-terminal-connect');
    this.btnTerminalRTS = document.getElementById('btn-terminal-rts');
    this.btnTerminalDTR = document.getElementById('btn-terminal-dtr');
    this.btnTerminalClear = document.getElementById('btn-terminal-clear');
    this.btnTerminalExport = document.getElementById('btn-terminal-export');
    this.inputTerminalCmd = document.getElementById('terminal-input');
    this.selectLineEnding = document.getElementById('terminal-line-ending');
    this.btnTerminalSend = document.getElementById('btn-terminal-send');

    // LED Status & Quick Macros
    this.elLedIndicatorPill = document.getElementById('led-indicator-pill');
    this.elLedBulb = document.getElementById('led-bulb');
    this.elLedLabel = document.getElementById('led-label');

    // Interactive Sketch Viewer
    this.sketchTabBtns = document.querySelectorAll('.sketch-tab-btn');
    this.elSketchDesc = document.getElementById('sketch-desc');
    this.elSketchFilename = document.getElementById('sketch-filename');
    this.elSketchCodePreview = document.getElementById('sketch-code-preview');
    this.btnCopySketch = document.getElementById('btn-copy-sketch');

    // Device Provisioner
    this.inputWifiSsid = document.getElementById('cfg-wifi-ssid');
    this.inputWifiPass = document.getElementById('cfg-wifi-pass');
    this.inputServerUrl = document.getElementById('cfg-server-url');
    this.inputDeviceId = document.getElementById('cfg-device-id');
    this.previewPayload = document.getElementById('cfg-payload-preview');
    this.btnSendProvision = document.getElementById('btn-send-provision');
    this.btnCopyProvision = document.getElementById('btn-copy-provision');

    // USB Driver Links & Helpers
    this.btnNavDrivers = document.getElementById('btn-nav-drivers');
    this.linkQuickDrivers = document.getElementById('link-quick-drivers');
    this.btnCopyLinuxCmd = document.getElementById('btn-copy-linux-cmd');
    this.driversSection = document.getElementById('drivers-section');

    this.monitor = new FuturonSerialMonitor(this.terminalOutput);
  }

  initTheme() {
    const savedTheme = localStorage.getItem('futuron_theme') || 'dark';
    this.applyTheme(savedTheme);
  }

  toggleTheme() {
    const currentTheme = document.documentElement.getAttribute('data-theme') || 'dark';
    const nextTheme = currentTheme === 'dark' ? 'light' : 'dark';
    localStorage.setItem('futuron_theme', nextTheme);
    this.applyTheme(nextTheme);
    this.logFlasher(`Switched interface to ${nextTheme.toUpperCase()} theme mode.`, 'info');
  }

  applyTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);
    if (theme === 'light') {
      if (this.iconThemeMoon) this.iconThemeMoon.classList.add('hidden');
      if (this.iconThemeSun) this.iconThemeSun.classList.remove('hidden');
    } else {
      if (this.iconThemeMoon) this.iconThemeMoon.classList.remove('hidden');
      if (this.iconThemeSun) this.iconThemeSun.classList.add('hidden');
    }
  }

  checkBrowserSupport() {
    const isSupported = FuturonESPLoader.isSupported();
    if (!isSupported) {
      this.elSupportBadge.className = 'badge badge-neutral';
      this.elSupportBadge.innerHTML = '<span class="dot"></span> Web Serial Unsupported';
      this.elBrowserAlert.classList.remove('hidden');
      this.btnQuickConnect.disabled = true;
    } else {
      if ('serial' in navigator) {
        navigator.serial.addEventListener('disconnect', () => {
          this.logFlasher('Serial hardware device unplugged from USB port.', 'warning');
          if (this.activeLoader && this.activeLoader.connected) {
            this.handleDisconnect();
          }
        });

        navigator.serial.addEventListener('connect', () => {
          this.logFlasher('New serial hardware device detected on USB port! Click Connect to auto-detect and sync hardware controls.', 'info');
        });
      }
    }
  }

  initEvents() {
    // Theme Toggle Handler
    this.initTheme();
    if (this.btnThemeToggle) {
      this.btnThemeToggle.addEventListener('click', () => this.toggleTheme());
    }

    // ESP32 Loader Callbacks
    this.espLoader.onLog = (msg, type) => this.logFlasher(msg, type);
    this.espLoader.onProgress = (w, t, p, s) => this.updateProgress(w, t, p, s);
    this.espLoader.onStatusChange = (conn, info) => this.handleConnectionStatusChange(conn, info);

    // STK500 Arduino Loader Callbacks
    this.stkLoader.onLog = (msg, type) => this.logFlasher(msg, type);
    this.stkLoader.onProgress = (w, t, p, s) => this.updateProgress(w, t, p, s);
    this.stkLoader.onStatusChange = (conn, info) => this.handleConnectionStatusChange(conn, info);

    // Tab Switching
    this.tabButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const targetTab = btn.getAttribute('data-tab');
        this.tabButtons.forEach(b => b.classList.remove('active'));
        this.tabPanes.forEach(p => p.classList.remove('active'));
        btn.classList.add('active');
        document.getElementById(targetTab).classList.add('active');
      });
    });

    // Target Chip Selection Change
    this.selectChip.addEventListener('change', () => {
      const target = this.selectChip.value;
      let mcuName = 'ESP32';

      if (target === 'esp32_devkit') {
        mcuName = 'ESP32 DevKit V1';
        this.selectBaud.value = '115200';
        this.elSummaryBaud.textContent = '115200';
        this.btnEraseFlash.disabled = false;
        if (this.elDevkitTip) this.elDevkitTip.classList.remove('hidden');
      } else {
        if (this.elDevkitTip) this.elDevkitTip.classList.add('hidden');
        if (target === 'uno' || target === 'nano_new') {
          mcuName = 'Arduino';
          this.selectBaud.value = '115200';
          this.elSummaryBaud.textContent = '115200';
          this.btnEraseFlash.disabled = true;
        } else if (target === 'nano_old') {
          mcuName = 'Arduino';
          this.selectBaud.value = '57600';
          this.elSummaryBaud.textContent = '57600';
          this.btnEraseFlash.disabled = true;
        } else if (target.startsWith('esp8266')) {
          mcuName = 'NodeMCU';
          this.selectBaud.value = '115200';
          this.elSummaryBaud.textContent = '115200';
          this.btnEraseFlash.disabled = false;
        } else {
          mcuName = 'ESP32';
          this.selectBaud.value = '921600';
          this.elSummaryBaud.textContent = '921600';
          this.btnEraseFlash.disabled = false;
        }
      }

      if (this.lblBtnConnect) this.lblBtnConnect.textContent = `Connect ${mcuName}`;
      if (this.lblBtnReset) this.lblBtnReset.textContent = `Reset ${mcuName}`;
      if (this.lblBtnFlash) this.lblBtnFlash.textContent = `Program & Flash ${mcuName} Firmware`;

      if (this.elHardwareAutoBadge && (!this.activeLoader || !this.activeLoader.connected)) {
        this.elHardwareAutoBadge.className = 'badge badge-neutral';
        this.elHardwareAutoBadge.innerHTML = `<span class="dot"></span> Target: ${mcuName}`;
      }

      this.renderPresets();
    });

    // Connect & Disconnect Buttons
    this.btnQuickConnect.addEventListener('click', () => this.handleConnect());
    this.btnQuickDisconnect.addEventListener('click', () => this.handleDisconnect());

    // USB Drivers Section Navigation
    const navigateToDrivers = (e) => {
      if (e) e.preventDefault();
      const guideTabBtn = document.querySelector('[data-tab="tab-guide"]');
      if (guideTabBtn) guideTabBtn.click();
      if (this.driversSection) {
        setTimeout(() => {
          this.driversSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.driversSection.style.transition = 'box-shadow 0.3s ease';
          this.driversSection.style.boxShadow = '0 0 25px var(--color-primary-glow)';
          setTimeout(() => {
            this.driversSection.style.boxShadow = '';
          }, 2000);
        }, 100);
      }
    };

    if (this.btnNavDrivers) {
      this.btnNavDrivers.addEventListener('click', navigateToDrivers);
    }
    if (this.linkQuickDrivers) {
      this.linkQuickDrivers.addEventListener('click', navigateToDrivers);
    }

    // Linux Permission Commands 1-Click Copy
    document.querySelectorAll('.btn-copy-cmd').forEach(btn => {
      btn.addEventListener('click', async () => {
        const cmd = btn.getAttribute('data-cmd') || 'sudo usermod -a -G dialout $USER';
        try {
          await navigator.clipboard.writeText(cmd);
          btn.classList.add('copied');
          const textSpan = btn.querySelector('.btn-copy-text');
          if (textSpan) textSpan.textContent = 'Copied!';
          setTimeout(() => {
            btn.classList.remove('copied');
            if (textSpan) textSpan.textContent = 'Copy';
          }, 2000);
          this.logFlasher(`Copied command: "${cmd}". Please restart your PC or log out & in for permissions to apply.`, 'info');
        } catch (err) {
          console.error('Clipboard copy failed:', err);
        }
      });
    });

    // Hardware Actions
    this.btnEraseFlash.addEventListener('click', () => this.handleEraseFlash());
    this.btnResetDevice.addEventListener('click', () => this.activeLoader.resetDevice());
    this.btnStartFlash.addEventListener('click', () => this.handleStartFlash());

    // Baudrate Change
    this.selectBaud.addEventListener('change', (e) => {
      this.elSummaryBaud.textContent = e.target.value;
    });

    // Mode Toggle (Presets vs Custom)
    this.btnModePreset.addEventListener('click', () => this.setFirmwareMode('preset'));
    this.btnModeCustom.addEventListener('click', () => this.setFirmwareMode('custom'));

    // Custom File Rows
    this.btnAddBinRow.addEventListener('click', () => this.addBinaryRow());
    this.binaryRowsContainer.addEventListener('click', (e) => {
      if (e.target.classList.contains('btn-remove-row')) {
        const row = e.target.closest('.binary-row');
        if (this.binaryRowsContainer.children.length > 1) {
          row.remove();
        }
      }
    });

    this.binaryRowsContainer.addEventListener('change', (e) => {
      if (e.target.classList.contains('file-input-bin')) {
        const file = e.target.files[0];
        const row = e.target.closest('.binary-row');
        const display = row.querySelector('.file-name-display');
        display.textContent = file ? file.name : 'No file selected';
      }
    });

    // Clear Logs
    this.btnClearLog.addEventListener('click', () => {
      this.flasherLogOutput.innerHTML = '';
    });

    // Serial Terminal Studio Controls
    this.btnTerminalConnect.addEventListener('click', () => this.handleTerminalConnectToggle());
    this.btnTerminalClear.addEventListener('click', () => this.monitor.clear());
    this.btnTerminalExport.addEventListener('click', () => this.monitor.exportLog());
    this.btnTerminalRTS.addEventListener('click', () => this.monitor.toggleRTS(true));
    this.btnTerminalDTR.addEventListener('click', () => this.monitor.resetChip());
    
    this.btnTerminalSend.addEventListener('click', () => this.sendTerminalCommand());
    this.inputTerminalCmd.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.sendTerminalCommand();
    });

    // Quick Command Macros for Serial LED & Diagnostics (Auto-connects if port is closed)
    document.querySelectorAll('.btn-macro').forEach(btn => {
      btn.addEventListener('click', async () => {
        const cmd = btn.getAttribute('data-cmd');
        const lineEnding = this.selectLineEnding.value;

        if (!this.monitor.connected) {
          try {
            await this.handleTerminalConnectToggle();
          } catch (err) {
            return;
          }
        }

        if (this.monitor.connected) {
          await this.monitor.send(cmd, lineEnding);
          this.logFlasher(`Transmitted serial command: "${cmd}"`, 'info');
        }
      });
    });

    // Monitor LED Status Change Listener
    this.monitor.onLedStatusChange = (status) => this.updateLedStatusUI(status);

    // Interactive Sketch Tabs Navigation
    if (this.sketchTabBtns) {
      this.sketchTabBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          this.sketchTabBtns.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          const sketchType = btn.getAttribute('data-sketch');
          this.renderSketchCode(sketchType);
        });
      });
    }

    // Copy Sketch Code Button
    if (this.btnCopySketch) {
      this.btnCopySketch.addEventListener('click', async () => {
        const sketch = SKETCH_TEMPLATES[this.currentSketchType];
        if (sketch && sketch.code) {
          try {
            await navigator.clipboard.writeText(sketch.code);
            this.btnCopySketch.textContent = '✅ Copied!';
            setTimeout(() => {
              this.btnCopySketch.textContent = '📋 Copy Sketch Code';
            }, 2000);
            this.logFlasher(`Copied ${sketch.filename} to clipboard.`, 'info');
          } catch (e) {
            console.error('Clipboard copy failed:', e);
          }
        }
      });
    }

    // Provisioner Controls
    const updatePayload = () => this.updateProvisionPayload();
    this.inputWifiSsid.addEventListener('input', updatePayload);
    this.inputWifiPass.addEventListener('input', updatePayload);
    this.inputServerUrl.addEventListener('input', updatePayload);
    this.inputDeviceId.addEventListener('input', updatePayload);

    this.btnCopyProvision.addEventListener('click', () => {
      navigator.clipboard.writeText(this.previewPayload.textContent);
      this.logFlasher('Copied provision payload to clipboard!', 'info');
    });

    this.btnSendProvision.addEventListener('click', async () => {
      if (this.monitor.connected) {
        await this.monitor.send(this.previewPayload.textContent);
        this.logFlasher('Sent provision command over serial port.', 'success');
      }
    });
  }

  updateLedStatusUI(status) {
    if (!this.elLedIndicatorPill || !this.elLedLabel) return;

    this.elLedIndicatorPill.classList.remove('led-on', 'led-off');

    if (status === 'ON') {
      this.elLedIndicatorPill.classList.add('led-on');
      this.elLedLabel.textContent = 'Hardware LED: ON (HIGH)';
    } else if (status === 'OFF') {
      this.elLedIndicatorPill.classList.add('led-off');
      this.elLedLabel.textContent = 'Hardware LED: OFF (LOW)';
    } else {
      this.elLedLabel.textContent = 'Hardware LED: UNKNOWN';
    }
  }

  renderSketchCode(sketchType) {
    this.currentSketchType = sketchType;
    const sketch = SKETCH_TEMPLATES[sketchType] || SKETCH_TEMPLATES.esp8266;

    if (this.elSketchDesc) {
      this.elSketchDesc.innerHTML = sketch.desc;
    }
    if (this.elSketchFilename) {
      this.elSketchFilename.textContent = sketch.filename;
    }
    if (this.elSketchCodePreview) {
      this.elSketchCodePreview.textContent = sketch.code;
    }
  }

  setFirmwareMode(mode) {
    this.firmwareMode = mode;
    if (mode === 'preset') {
      this.btnModePreset.classList.add('active');
      this.btnModeCustom.classList.remove('active');
      this.viewPreset.classList.remove('hidden');
      this.viewCustom.classList.add('hidden');
    } else {
      this.btnModeCustom.classList.add('active');
      this.btnModePreset.classList.remove('active');
      this.viewCustom.classList.remove('hidden');
      this.viewPreset.classList.add('hidden');
    }
  }

  isArduinoSelected() {
    const val = this.selectChip.value;
    return val === 'uno' || val === 'nano_new' || val === 'nano_old';
  }

  renderPresets() {
    this.firmwareListContainer.innerHTML = '';
    const targetVal = this.selectChip.value;
    let targetArch = 'esp32';
    if (this.isArduinoSelected()) {
      targetArch = 'avr';
    } else if (targetVal.startsWith('esp8266')) {
      targetArch = 'esp8266';
    }

    const filtered = FUTURON_FIRMWARES.filter(fw => fw.arch === targetArch);
    if (filtered.length > 0) {
      this.selectedPresetId = filtered[0].id;
    }

    filtered.forEach(fw => {
      const card = document.createElement('div');
      card.className = `fw-card ${fw.id === this.selectedPresetId ? 'selected' : ''}`;
      card.dataset.id = fw.id;

      card.innerHTML = `
        <div class="fw-card-title">${fw.title}</div>
        <div class="fw-card-desc">${fw.description}</div>
        <div class="fw-card-tags">
          <span class="tag">${fw.version}</span>
          <span class="tag">Offset: ${fw.offset}</span>
          ${fw.tags.map(t => `<span class="tag">${t}</span>`).join('')}
        </div>
      `;

      card.addEventListener('click', () => {
        document.querySelectorAll('.fw-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        this.selectedPresetId = fw.id;
        this.logFlasher(`Selected preset: ${fw.title}`, 'info');
      });

      this.firmwareListContainer.appendChild(card);
    });
  }

  addBinaryRow() {
    const row = document.createElement('div');
    row.className = 'binary-row';
    row.innerHTML = `
      <div class="file-input-wrapper">
        <input type="file" class="file-input-bin" accept=".bin,.hex" />
        <button class="btn btn-secondary btn-file-trigger">Choose .BIN / .HEX File</button>
        <span class="file-name-display">No file selected</span>
      </div>
      <div class="address-input-wrapper">
        <span class="prefix">0x</span>
        <input type="text" class="form-control addr-input" value="0" placeholder="Offset e.g. 0" />
      </div>
      <button class="btn-icon btn-remove-row" title="Remove row">✕</button>
    `;
    this.binaryRowsContainer.appendChild(row);
  }

  async handleConnect() {
    const baud = this.selectBaud.value;
    const targetChip = this.selectChip.value;

    try {
      this.btnQuickConnect.disabled = true;
      this.btnQuickConnect.innerHTML = 'Select Serial Port...';

      // Always request port from browser picker dialog so user can explicitly pick their port
      this.logFlasher('Opening Web Serial port chooser dialog...', 'info');
      const selectedPort = await navigator.serial.requestPort();

      if (!selectedPort) {
        throw new Error('No serial port selected.');
      }

      this.btnQuickConnect.innerHTML = 'Connecting...';

      if (targetChip === 'auto' || targetChip === 'esp32_devkit' || targetChip.startsWith('esp32')) {
        this.logFlasher(`Probing ESPTool protocol on selected port (${baud} baud)...`, 'info');
        try {
          this.activeLoader = this.espLoader;
          const chipTarget = targetChip === 'esp32_devkit' ? 'esp32' : targetChip;
          await this.espLoader.connect(baud, chipTarget, selectedPort);
        } catch (espErr) {
          this.logFlasher(`Initial sync at ${baud} baud failed (${espErr.message}). Retrying at safe 115200 baud for ESP32 DevKit V1...`, 'warning');
          try {
            this.activeLoader = this.espLoader;
            await this.espLoader.connect(115200, 'esp32', selectedPort);
          } catch (espRetryErr) {
            if (targetChip === 'auto') {
              this.logFlasher('ESP32 probe failed. Probing Arduino STK500 protocol on selected port...', 'warning');
              try {
                this.activeLoader = this.stkLoader;
                await this.stkLoader.connect(115200, 'uno', selectedPort);
              } catch (stkErr) {
                throw new Error(`Could not sync with selected port. If using ESP32 DevKit V1, hold the BOOT button on your board for 2 seconds while clicking Connect.`);
              }
            } else {
              throw new Error(`ESP32 DevKit V1 sync failed. Please hold down the BOOT button on your ESP32 board and click Connect again.`);
            }
          }
        }
      } else if (this.isArduinoSelected()) {
        this.activeLoader = this.stkLoader;
        const boardType = targetChip === 'uno' ? 'uno' : 'nano';
        await this.stkLoader.connect(baud, boardType, selectedPort);
      } else {
        this.activeLoader = this.espLoader;
        await this.espLoader.connect(baud, targetChip, selectedPort);
      }
    } catch (err) {
      if (err.name === 'NotFoundError' || err.message.includes('No port selected')) {
        this.logFlasher('Serial port selection cancelled by user.', 'warning');
      } else {
        this.logFlasher(`Port connection error: ${err.message}`, 'error');
        this.logFlasher('💡 Port missing or not recognized? Download CP210x or CH340 Drivers from the USB Drivers Guide tab.', 'info');
      }
      this.btnQuickConnect.disabled = false;
      this.btnQuickConnect.innerHTML = `
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
        Connect Board
      `;
    }
  }

  async handleDisconnect() {
    await this.activeLoader.disconnect();
  }

  autoUpdateHardwareControls(chipInfo) {
    if (!chipInfo || !chipInfo.chipName) return;

    const chipNameUpper = chipInfo.chipName.toUpperCase();
    let detectedValue = 'esp32';
    let defaultBaud = '921600';
    let canErase = true;

    if (chipNameUpper.includes('ESP32-S3') || chipNameUpper.includes('ESP32S3')) {
      detectedValue = 'esp32s3';
      defaultBaud = '921600';
    } else if (chipNameUpper.includes('ESP32-S2') || chipNameUpper.includes('ESP32S2')) {
      detectedValue = 'esp32s2';
      defaultBaud = '921600';
    } else if (chipNameUpper.includes('ESP32-C3') || chipNameUpper.includes('ESP32C3')) {
      detectedValue = 'esp32c3';
      defaultBaud = '921600';
    } else if (chipNameUpper.includes('ESP32-C6') || chipNameUpper.includes('ESP32C6')) {
      detectedValue = 'esp32c6';
      defaultBaud = '921600';
    } else if (chipNameUpper.includes('8266') || chipNameUpper.includes('NODEMCU')) {
      detectedValue = 'esp8266';
      defaultBaud = '115200';
    } else if (chipNameUpper.includes('UNO')) {
      detectedValue = 'uno';
      defaultBaud = '115200';
      canErase = false;
    } else if (chipNameUpper.includes('NANO')) {
      detectedValue = 'nano_new';
      defaultBaud = '115200';
      canErase = false;
    } else if (chipNameUpper.includes('ESP32')) {
      detectedValue = 'esp32';
      defaultBaud = '921600';
    }

    // Automatically update target chip select dropdown
    if (this.selectChip) {
      this.selectChip.value = detectedValue;
    }

    // Automatically update flashing baud rate dropdown & summary badge
    if (this.selectBaud) {
      this.selectBaud.value = defaultBaud;
    }
    if (this.elSummaryBaud) {
      this.elSummaryBaud.textContent = defaultBaud;
    }

    // Automatically update Erase Flash button state
    if (this.btnEraseFlash) {
      this.btnEraseFlash.disabled = !canErase;
      this.btnEraseFlash.title = canErase
        ? `Erase full chip flash memory on ${chipInfo.chipName}`
        : `Erase Flash disabled for ${chipInfo.chipName} (Page overwrite is automatic on AVR)`;
    }

    // Automatically update Auto-Sync status badge
    if (this.elHardwareAutoBadge) {
      this.elHardwareAutoBadge.className = 'badge badge-success';
      this.elHardwareAutoBadge.innerHTML = `<span class="dot"></span> Auto-Synced: ${chipInfo.chipName}`;
    }

    if (this.elChipSelectHelp) {
      this.elChipSelectHelp.textContent = `⚡ Auto-detected & synced to ${chipInfo.chipName}`;
      this.elChipSelectHelp.style.color = 'var(--color-success)';
    }

    // Dynamic button labels
    if (this.lblBtnConnect) this.lblBtnConnect.textContent = `Connected (${chipInfo.chipName})`;
    if (this.lblBtnReset) this.lblBtnReset.textContent = `Reset ${chipInfo.chipName}`;
    if (this.lblBtnFlash) this.lblBtnFlash.textContent = `Program & Flash ${chipInfo.chipName} Firmware`;

    // Re-render firmware presets for detected board architecture
    this.renderPresets();

    this.logFlasher(`Serial hardware controls automatically updated for connected target device: ${chipInfo.chipName} (${defaultBaud} baud).`, 'success');
  }

  resetHardwareControlsUI() {
    if (this.elHardwareAutoBadge) {
      this.elHardwareAutoBadge.className = 'badge badge-neutral';
      this.elHardwareAutoBadge.innerHTML = '<span class="dot"></span> Auto-Sync Ready';
    }
    if (this.elChipSelectHelp) {
      this.elChipSelectHelp.textContent = '⚡ Auto-configures baud rate & options when device connects';
      this.elChipSelectHelp.style.color = 'var(--color-primary)';
    }
    if (this.lblBtnConnect) this.lblBtnConnect.textContent = 'Connect Device';
    if (this.lblBtnReset) this.lblBtnReset.textContent = 'Reset Device';
    if (this.lblBtnFlash) this.lblBtnFlash.textContent = 'Program & Flash Firmware';
  }

  handleConnectionStatusChange(connected, chipInfo) {
    if (connected && chipInfo) {
      // Automatically update serial hardware controls according to target device connected
      this.autoUpdateHardwareControls(chipInfo);

      this.elStatusBadge.className = 'badge badge-active';
      this.elStatusBadge.innerHTML = `<span class="dot"></span> Connected (${chipInfo.chipName})`;

      this.elSummaryChip.textContent = chipInfo.chipName;
      this.elSummaryMac.textContent = chipInfo.mac;
      this.elSummaryFlash.textContent = chipInfo.flashSize;

      this.elChipModel.textContent = chipInfo.chipName;
      this.elChipRevision.textContent = chipInfo.revision;
      this.elChipMac.textContent = chipInfo.mac;
      this.elChipFlashSize.textContent = chipInfo.flashSize;
      this.elChipCrystal.textContent = chipInfo.crystalFreq;
      this.elChipFeatures.textContent = this.isArduinoSelected() ? 'GPIO Pins, ADC, SPI, I2C, UART' : 'Wi-Fi, Bluetooth BLE, Dual Core';

      this.btnQuickConnect.classList.add('hidden');
      this.btnQuickDisconnect.classList.remove('hidden');

      this.btnResetDevice.disabled = false;
      this.btnStartFlash.disabled = false;
      this.btnSendProvision.disabled = false;
    } else {
      this.resetHardwareControlsUI();

      this.elStatusBadge.className = 'badge badge-neutral';
      this.elStatusBadge.innerHTML = '<span class="dot"></span> Disconnected';

      this.elSummaryChip.textContent = 'None Connected';
      this.elSummaryMac.textContent = '--:--:--:--:--:--';
      this.elSummaryFlash.textContent = 'Unknown';

      this.elChipModel.textContent = '--';
      this.elChipRevision.textContent = '--';
      this.elChipMac.textContent = '--';
      this.elChipFlashSize.textContent = '--';
      this.elChipCrystal.textContent = '--';
      this.elChipFeatures.textContent = '--';

      this.btnQuickConnect.classList.remove('hidden');
      this.btnQuickConnect.disabled = false;
      this.btnQuickConnect.innerHTML = `
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
        Connect Board
      `;
      this.btnQuickDisconnect.classList.add('hidden');

      this.btnEraseFlash.disabled = true;
      this.btnResetDevice.disabled = true;
      this.btnStartFlash.disabled = true;
      this.btnSendProvision.disabled = true;
    }
  }

  getTargetDeviceName() {
    if (this.activeLoader && this.activeLoader.chipInfo && this.activeLoader.chipInfo.chipName) {
      return this.activeLoader.chipInfo.chipName;
    }
    const val = this.selectChip.value;
    if (val.startsWith('esp8266')) return 'ESP8266 / NodeMCU';
    if (val === 'esp32_devkit') return 'ESP32 DevKit V1';
    if (val.startsWith('esp32')) return val.toUpperCase();
    if (val === 'uno') return 'Arduino Uno';
    if (val.startsWith('nano')) return 'Arduino Nano';
    return 'ESP Microcontroller';
  }

  showFlashingError(targetMcu, err) {
    const isNoiseOrPacketHead = err.message && (err.message.includes('Invalid head of packet') || err.message.includes('Possible serial noise') || err.message.includes('No serial data received'));
    
    if (isNoiseOrPacketHead) {
      this.logFlasher(`❌ ${targetMcu} Bootloader Sync Error: ${err.message}`, 'error');
      this.logFlasher('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'warning');
      this.logFlasher('💡 DIAGNOSTIC & FIX (Invalid head of packet / noise):', 'warning');
      this.logFlasher('   Cause: Board is outputting serial data instead of being in ROM bootloader mode.', 'warning');
      
      const is8266 = targetMcu.toUpperCase().includes('8266') || targetMcu.toUpperCase().includes('NODE');
      if (is8266) {
        this.logFlasher('👉 ESP8266 / NodeMCU Quick Fix:', 'info');
        this.logFlasher('   1. Ensure Flashing Baud Rate is set to 115200 (high baud rates fail on ESP8266).', 'info');
        this.logFlasher('   2. Hold FLASH button (GPIO0 / D3) on NodeMCU.', 'info');
        this.logFlasher('   3. Tap RST button once while still holding FLASH.', 'info');
        this.logFlasher('   4. Release FLASH button.', 'info');
        this.logFlasher('   5. Click "Program & Flash" again.', 'info');
      } else {
        this.logFlasher('👉 ESP32 Quick Fix:', 'info');
        this.logFlasher('   1. Hold BOOT button (IO0) on ESP32 board.', 'info');
        this.logFlasher('   2. Tap EN / RST button once while still holding BOOT.', 'info');
        this.logFlasher('   3. Release BOOT button.', 'info');
        this.logFlasher('   4. Try 115200 baud if 921600 fails.', 'info');
      }
      this.logFlasher('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'warning');

      const bootloaderBtnName = is8266 ? 'FLASH (GPIO0)' : 'BOOT (IO0)';
      alert(`⚠️ ${targetMcu} Flashing Error:\n${err.message}\n\n💡 Reason:\nThe chip sent regular UART text (byte 0x73/'s') instead of bootloader sync packets.\n\n🛠️ How to Fix:\n1. Ensure Baud Rate is 115200.\n2. Put board in Bootloader Mode manually:\n   • Hold ${bootloaderBtnName} button\n   • Press & release RST/EN button\n   • Release ${bootloaderBtnName} button\n3. Click "Program & Flash Firmware" again.`);
    } else {
      this.logFlasher(`❌ ${targetMcu} Flashing error: ${err.message}`, 'error');
      alert(`${targetMcu} Flashing error: ${err.message}`);
    }
  }

  async handleEraseFlash() {
    if (this.isArduinoSelected()) {
      alert('Arduino AVR microcontrollers automatically overwrite flash memory page by page.');
      return;
    }

    const mcu = this.getTargetDeviceName();
    if (confirm(`Are you sure you want to completely erase all flash memory on the ${mcu} chip?`)) {
      this.btnEraseFlash.disabled = true;
      try {
        await this.espLoader.eraseFlash();
      } catch (err) {
        this.showFlashingError(mcu, err);
      } finally {
        this.btnEraseFlash.disabled = false;
      }
    }
  }

  async handleStartFlash() {
    if (this.isArduinoSelected()) {
      await this.handleStartArduinoFlash();
      return;
    }

    const mcuName = this.getTargetDeviceName();

    // ESP Flashing Execution
    let filesToFlash = [];
    if (this.firmwareMode === 'preset') {
      const preset = FUTURON_FIRMWARES.find(f => f.id === this.selectedPresetId);
      if (!preset) {
        alert('Please select a valid preset firmware.');
        return;
      }

      this.logFlasher(`Preparing preset firmware binary: ${preset.title}...`, 'info');
      
      let binBuffer;
      try {
        const resp = await fetch(preset.downloadUrl);
        if (resp.ok) {
          const arrayBuf = await resp.arrayBuffer();
          binBuffer = new Uint8Array(arrayBuf);
        } else {
          throw new Error('Preset binary file not found on server, generating synthetic firmware payload.');
        }
      } catch (e) {
        this.logFlasher(e.message, 'warning');
        binBuffer = createSyntheticBinary(131072, preset.title);
      }

      const offsetNum = parseInt(preset.offset.replace('0x', ''), 16);
      filesToFlash.push({
        data: binBuffer,
        address: offsetNum
      });
    } else {
      const rows = this.binaryRowsContainer.querySelectorAll('.binary-row');
      for (const row of rows) {
        const fileInput = row.querySelector('.file-input-bin');
        const addrInput = row.querySelector('.addr-input');

        if (fileInput.files && fileInput.files[0]) {
          const file = fileInput.files[0];
          const arrayBuf = await file.arrayBuffer();
          const addrHex = addrInput.value.trim().replace(/^0x/i, '');
          const address = parseInt(addrHex, 16) || 0;

          filesToFlash.push({
            data: new Uint8Array(arrayBuf),
            address: address
          });
        }
      }

      if (filesToFlash.length === 0) {
        alert('Please select at least one valid .BIN file to flash.');
        return;
      }
    }

    const eraseBefore = this.chkEraseBeforeFlash.checked;

    try {
      this.btnStartFlash.disabled = true;
      this.startTimer();
      await this.espLoader.writeFlash(filesToFlash, eraseBefore);
      this.logFlasher(`🎉 Flashing Complete! Resetting ${mcuName} to boot user sketch...`, 'success');
      await this.espLoader.resetDevice();
      this.logFlasher('⚡ Serial port released. You can now open "Serial Studio & Real-time Console" tab at 115200 baud to view live Serial.print() logs.', 'info');
    } catch (err) {
      this.showFlashingError(mcuName, err);
    } finally {
      this.stopTimer();
      this.btnStartFlash.disabled = false;
    }
  }

  async handleStartArduinoFlash() {
    let hexBuffer = null;

    if (this.firmwareMode === 'preset') {
      const preset = FUTURON_FIRMWARES.find(f => f.id === this.selectedPresetId);
      if (!preset) {
        alert('Please select a valid Arduino preset firmware.');
        return;
      }

      this.logFlasher(`Preparing Arduino preset HEX: ${preset.title}...`, 'info');

      let hexText = '';
      try {
        const resp = await fetch(preset.downloadUrl);
        if (resp.ok) {
          hexText = await resp.text();
        } else {
          throw new Error('Preset HEX file not found, using synthetic Arduino sketch.');
        }
      } catch (e) {
        this.logFlasher(e.message, 'warning');
        hexText = createSyntheticHex(preset.title);
      }

      hexBuffer = parseHex(hexText);
    } else {
      // Custom .HEX / .BIN file
      const row = this.binaryRowsContainer.querySelector('.binary-row');
      const fileInput = row.querySelector('.file-input-bin');

      if (fileInput.files && fileInput.files[0]) {
        const file = fileInput.files[0];
        const content = await file.text();

        if (file.name.endsWith('.hex') || content.trim().startsWith(':')) {
          hexBuffer = parseHex(content);
        } else {
          const arrayBuf = await file.arrayBuffer();
          hexBuffer = {
            data: new Uint8Array(arrayBuf),
            size: arrayBuf.byteLength
          };
        }
      }

      if (!hexBuffer || hexBuffer.size === 0) {
        alert('Please select a valid .HEX or .BIN file to flash to Arduino.');
        return;
      }
    }

    try {
      this.btnStartFlash.disabled = true;
      this.startTimer();
      await this.stkLoader.writeFlash(hexBuffer);
      this.logFlasher('🎉 Flashing Complete! Resetting Arduino to boot user sketch...', 'success');
      await this.stkLoader.resetDevice();
      this.logFlasher('⚡ Serial port released. Switch to "Serial Studio & Real-time Console" tab at 115200 baud to view live Serial.print() logs.', 'info');
    } catch (err) {
      alert(`Arduino Flashing error: ${err.message}`);
    } finally {
      this.stopTimer();
      this.btnStartFlash.disabled = false;
    }
  }

  updateProgress(written, total, percentage, speedKbps) {
    this.elProgressPercentage.textContent = `${percentage}%`;
    this.elProgressBarFill.style.width = `${percentage}%`;
    this.elMetricSpeed.textContent = `${speedKbps} kB/s`;
    this.elMetricBytes.textContent = `${(written / 1024).toFixed(1)} / ${(total / 1024).toFixed(1)} kB`;
    this.elProgressStatus.textContent = percentage === 100 ? 'Status: Complete' : 'Status: Writing Flash...';
  }

  startTimer() {
    this.startTime = Date.now();
    this.timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
      const mins = String(Math.floor(elapsed / 60)).padStart(2, '0');
      const secs = String(elapsed % 60).padStart(2, '0');
      this.elMetricTime.textContent = `${mins}:${secs}`;
    }, 1000);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  logFlasher(msg, type = 'info') {
    const entry = document.createElement('div');
    entry.className = `log-entry ${type}`;
    const time = new Date().toLocaleTimeString();
    entry.textContent = `[${time}] ${msg}`;
    this.flasherLogOutput.appendChild(entry);
    this.flasherLogOutput.scrollTop = this.flasherLogOutput.scrollHeight;
  }

  async handleTerminalConnectToggle() {
    if (this.monitor.connected) {
      await this.monitor.disconnect();
      this.btnTerminalConnect.textContent = 'Open Monitor Port';
      this.btnTerminalConnect.className = 'btn btn-primary btn-sm';
      return false;
    } else {
      let sharedPort = null;
      if (this.activeLoader && this.activeLoader.connected) {
        sharedPort = this.activeLoader.port;
        this.logFlasher('Releasing flasher serial port lock to open Serial Monitor...', 'info');
        try {
          await this.activeLoader.disconnect();
        } catch (e) {}
      }

      const baud = this.selectTerminalBaud.value;
      try {
        await this.monitor.connect(baud, sharedPort);
        this.btnTerminalConnect.textContent = 'Close Port';
        this.btnTerminalConnect.className = 'btn btn-danger btn-sm';
        return true;
      } catch (err) {
        if (err.name !== 'NotFoundError') {
          this.logFlasher(`Serial monitor connect error: ${err.message}`, 'error');
        }
        throw err;
      }
    }
  }

  async sendTerminalCommand() {
    const cmd = this.inputTerminalCmd.value.trim();
    const ending = this.selectLineEnding.value;
    if (!cmd) return;

    if (!this.monitor.connected) {
      try {
        await this.handleTerminalConnectToggle();
      } catch (err) {
        return;
      }
    }

    if (this.monitor.connected) {
      await this.monitor.send(cmd, ending);
      this.inputTerminalCmd.value = '';
    }
  }

  updateProvisionPayload() {
    const ssid = this.inputWifiSsid.value;
    const pass = this.inputWifiPass.value;
    const server = this.inputServerUrl.value;
    const id = this.inputDeviceId.value;

    const payload = `SET_WIFI:SSID="${ssid}",PASS="${pass}",SERVER="${server}",ID="${id}"`;
    this.previewPayload.textContent = payload;
  }
}

// Instantiate app on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new FuturonFlashHubApp();
});
